/*!
 * Form tracking for Snowplow v4.6.9 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { addEventListener, getCssClasses, flushPendingCookies, getFilterByClass, getFilterByName } from '@snowplow/browser-tracker-core';
import { buildFormFocusOrChange, resolveDynamicContext, buildFormSubmission } from '@snowplow/tracker-core';

/** Events to capture in form tracking */
var FormTrackingEvent;
(function (FormTrackingEvent) {
    /** Form field changed event */
    FormTrackingEvent["CHANGE_FORM"] = "change_form";
    /** Form field focused event */
    FormTrackingEvent["FOCUS_FORM"] = "focus_form";
    /** Form submitted event */
    FormTrackingEvent["SUBMIT_FORM"] = "submit_form";
})(FormTrackingEvent || (FormTrackingEvent = {}));
var defaultFormTrackingEvents = [
    FormTrackingEvent.CHANGE_FORM,
    FormTrackingEvent.FOCUS_FORM,
    FormTrackingEvent.SUBMIT_FORM,
];
var defaultTransformFn = function (x) { return x; };
var _focusListeners = {};
var _changeListeners = {};
var _submitListeners = {};
var _targets = {};
var _captures = {};
/**
 * Add submission/focus/change event listeners to page for forms and elements according to `configuration`
 *
 * @param tracker The tracker instance the listener belongs to that will be used to track events
 * @param configuration Plugin configuration controlling the events to track and forms/fields to target or transform
 */
function addFormListeners(tracker, configuration) {
    var _a, _b;
    var options = configuration.options, context = configuration.context, config = getConfigurationForOptions(options);
    var events = (_a = options === null || options === void 0 ? void 0 : options.events) !== null && _a !== void 0 ? _a : defaultFormTrackingEvents;
    var useCapture = (_captures[tracker.id] = (_b = options === null || options === void 0 ? void 0 : options.useCapture) !== null && _b !== void 0 ? _b : true);
    var targets = (_targets[tracker.id] = getTargetList(options === null || options === void 0 ? void 0 : options.targets, config.forms));
    if (events.indexOf(FormTrackingEvent.FOCUS_FORM) !== -1) {
        _focusListeners[tracker.id] = getFormChangeListener(tracker, config, FormTrackingEvent.FOCUS_FORM, context);
        targets.forEach(function (target) { return addEventListener(target, 'focus', _focusListeners[tracker.id], true); }); // focus does not bubble
    }
    if (events.indexOf(FormTrackingEvent.CHANGE_FORM) !== -1) {
        _changeListeners[tracker.id] = getFormChangeListener(tracker, config, FormTrackingEvent.CHANGE_FORM, context);
        targets.forEach(function (target) { return addEventListener(target, 'change', _changeListeners[tracker.id], useCapture); });
    }
    if (events.indexOf(FormTrackingEvent.SUBMIT_FORM) !== -1) {
        _submitListeners[tracker.id] = getFormSubmissionListener(tracker, config, context);
        targets.forEach(function (target) { return addEventListener(target, 'submit', _submitListeners[tracker.id], useCapture); });
    }
}
/**
 * Builds a list of targets for the plugin event listeners
 *
 * The list can include any specifically provided targets, and will be extended to include the root nodes of any explicit HTMLFormElements provided
 * With neither provided, defaults to the current page's `document` element
 *
 * @param configTargets Explicitly configured list of event target listeners, if any
 * @param forms Explicitly configured list of form elements to track, if any
 * @returns List of EventTargets to add the listener to
 */
function getTargetList(configTargets, forms) {
    // we attach to document rather than window because the window focus event occurs more often than we require
    var targets = configTargets !== null && configTargets !== void 0 ? configTargets : [document];
    if (forms) {
        Array.prototype.forEach.call(forms, function (form) {
            targets.push(form.ownerDocument.documentElement);
        });
    }
    return targets;
}
/**
 * Remove all submission/focus/change event listeners from page that have been added via a call to `addFormListeners`
 *
 * @param tracker The tracker instance the listener belongs to that will be used to track events
 */
function removeFormListeners(tracker) {
    var _a, _b;
    var targets = (_a = _targets[tracker.id]) !== null && _a !== void 0 ? _a : [document];
    var useCapture = (_b = _captures[tracker.id]) !== null && _b !== void 0 ? _b : true;
    targets.forEach(function (target) {
        if (_focusListeners[tracker.id])
            target.removeEventListener('focus', _focusListeners[tracker.id], true); // focus does not bubble
        if (_changeListeners[tracker.id])
            target.removeEventListener('change', _changeListeners[tracker.id], useCapture);
        if (_submitListeners[tracker.id])
            target.removeEventListener('submit', _submitListeners[tracker.id], useCapture);
    });
}
/**
 * Check if forms array is a collection of HTML form elements or a filter or undefined
 */
function isCollectionOfHTMLFormElements(forms) {
    return forms != null && Array.prototype.slice.call(forms).length > 0;
}
/**
 * Typeguard for `element` to see if it appears to be the HTMLElement with tagName `type`
 *
 * instanceof checks don't work for cross-document nodes, which this plugin supports
 *
 * @param elem Object to check element type
 * @param type Element type we're checking for
 * @returns If `element` is an element with tagName `type`
 */
function isElement(elem, type) {
    if (typeof elem === 'object' && elem) {
        if ('tagName' in elem && typeof elem['tagName'] === 'string') {
            return elem.tagName.toUpperCase() === type;
        }
    }
    return false;
}
/**
 * Determine if given object is a `TrackedHTMLElement` or not
 *
 * @param element Value to determine
 * @returns If `element` is `TrackedHTMLElement`
 */
function isTrackableElement(element) {
    return isElement(element, 'INPUT') || isElement(element, 'SELECT') || isElement(element, 'TEXTAREA');
}
/**
 * Configures form tracking: which forms and fields will be tracked, and the context to attach
 *
 * @param options User-supplied configuration
 * @returns Final configuration incorporating defaults
 */
function getConfigurationForOptions(options) {
    if (options) {
        var formFilter = function (_) { return true; };
        var forms = null;
        if (isCollectionOfHTMLFormElements(options.forms)) {
            // options.forms is an explicity allowlist of HTML form elements
            forms = options.forms;
        }
        else {
            // options.forms is null or a filter
            formFilter = getFilterByClass(options.forms);
        }
        return {
            forms: forms,
            formFilter: formFilter,
            fieldFilter: getFilterByName(options.fields),
            fieldTransform: getTransform(options.fields),
        };
    }
    else {
        return {
            forms: null,
            formFilter: function () { return true; },
            fieldFilter: function () { return true; },
            fieldTransform: defaultTransformFn,
        };
    }
}
/**
 * Check if the found target element is included in the explicit form allowlist, if provided.
 *
 * @param target A `form` element to check if we're allowed to track.
 * @param allowed An optional list of form elements we want to track against.
 * @returns True if there is no allowlist or the `target` is in the allowlist, false otherwise.
 */
function explicitlyAllowedForm(target, allowed) {
    if (!allowed)
        return true;
    for (var i = 0; i < allowed.length; i++) {
        if (allowed[i].isSameNode(target))
            return true;
    }
    return false;
}
/**
 * Convert a criterion object to a transform function
 *
 * @param criterion
 * @returns Transformation function if provided in `criterion`, or a default identity function
 */
function getTransform(criterion) {
    if (criterion && typeof criterion.transform === 'function') {
        return criterion.transform;
    }
    return defaultTransformFn;
}
/**
 * Get an identifier for a form or `TrackedHTMLElement`
 *
 * @param elt Element to identify
 * @returns Identifier for `elt`
 */
function getElementIdentifier(elt) {
    var properties = ['name', 'id', 'type', 'nodeName'];
    for (var _i = 0, properties_1 = properties; _i < properties_1.length; _i++) {
        var propName = properties_1[_i];
        if (elt[propName] && typeof elt[propName] === 'string') {
            return elt[propName];
        }
    }
    return null;
}
/**
 * Discovers the parent form in which an element is contained
 *
 * @param elt Child control to identify the owning form for
 * @returns The form element this control belongs to or null if not found
 */
function getParentForm(elt) {
    if (elt && elt.form)
        return elt.form;
    var parent = elt;
    while (parent) {
        if (isElement(parent, 'FORM')) {
            return parent;
        }
        parent = parent.parentNode;
    }
    return parent;
}
/**
 * Returns a list of the `TrackedHTMLElement`s inside a form along with their values
 *
 * @param elt Form element to get the control elements for
 * @returns Array of wrapped control elements belonging to the form
 */
function getInnerFormElements(elt) {
    var innerElements = [];
    Array.prototype.forEach.call(elt.elements, function (child) {
        if (!isTrackableElement(child))
            return;
        var inputType = (child.type || 'text').toLowerCase();
        // submit and image are roughly equivalent
        if (inputType === 'submit' || inputType === 'image') {
            return;
        }
        var elementJson = {
            elementData: {
                name: getElementIdentifier(child),
                value: child.value,
                nodeName: child.nodeName,
            },
            originalElement: child,
        };
        if (isElement(child, 'INPUT')) {
            elementJson.elementData.type = inputType;
            if (inputType === 'password' || ((inputType === 'checkbox' || inputType === 'radio') && !child.checked)) {
                elementJson.elementData.value = null;
            }
        }
        innerElements.push(elementJson);
    });
    return innerElements;
}
/**
 * Create closure function to handle form field change/focus event
 *
 * @param tracker The tracker instance to generate the event with
 * @param config Plugin configuration
 * @param event_type Type of event to generate
 * @param context List of entities or context generators to evaluate with the event
 * @returns A form change/focus handler
 */
function getFormChangeListener(tracker, config, event_type, context) {
    return function (e) {
        var _a, _b;
        var target = e.composed ? e.composedPath()[0] : e.target;
        // `change` and `submit` are not composed and are thus invisible to us
        // bind late to the forms/field directly on field focus in this case
        if (target !== e.target && e.composed && isTrackableElement(target)) {
            if (target.form) {
                if (_changeListeners[tracker.id])
                    addEventListener(target.form, 'change', _changeListeners[tracker.id], _captures[tracker.id]);
                if (_submitListeners[tracker.id])
                    addEventListener(target.form, 'submit', _submitListeners[tracker.id], _captures[tracker.id]);
            }
            else {
                if (_changeListeners[tracker.id])
                    addEventListener(target, 'change', _changeListeners[tracker.id], _captures[tracker.id]);
            }
        }
        if (isTrackableElement(target) && config.fieldFilter(target)) {
            var value = null;
            var type = null;
            if (isElement(target, 'INPUT')) {
                type = (target.type || 'text').toLowerCase();
                value =
                    (type === 'checkbox' && !target.checked) || type === 'password'
                        ? null
                        : config.fieldTransform(target.value, target, target);
            }
            else {
                value = config.fieldTransform(target.value, target, target);
            }
            var form = getParentForm(target);
            if (!(form && config.formFilter(form) && explicitlyAllowedForm(form, config.forms)))
                return;
            if (event_type === 'change_form' || (type !== 'checkbox' && type !== 'radio')) {
                tracker.core.track(buildFormFocusOrChange({
                    schema: event_type,
                    formId: (_a = getElementIdentifier(form !== null && form !== void 0 ? form : {})) !== null && _a !== void 0 ? _a : '',
                    elementId: (_b = getElementIdentifier(target)) !== null && _b !== void 0 ? _b : '',
                    nodeName: target.nodeName,
                    type: type,
                    elementClasses: getCssClasses(target),
                    value: value !== null && value !== void 0 ? value : null,
                }), resolveDynamicContext(context, target, type, value));
            }
        }
    };
}
/**
 * Create closure function to handle form submission event
 *
 * @param tracker The tracker instance to generate the event with
 * @param config Plugin configuration
 * @param context List of entities or context generators to evaluate with the event
 * @returns A form submit handler
 */
function getFormSubmissionListener(tracker, config, context) {
    return function (_a) {
        var _b;
        var target = _a.target;
        if (isElement(target, 'FORM') && config.formFilter(target) && explicitlyAllowedForm(target, config.forms)) {
            var elementsData_1 = [];
            getInnerFormElements(target).forEach(function (_a) {
                var elementData = _a.elementData, originalElement = _a.originalElement;
                if (config.fieldFilter(originalElement) && originalElement.type.toLowerCase() !== 'password') {
                    elementData.value = config.fieldTransform(elementData.value, elementData, originalElement);
                    elementsData_1.push(elementData);
                }
            });
            tracker.core.track(buildFormSubmission({
                formId: (_b = getElementIdentifier(target)) !== null && _b !== void 0 ? _b : '',
                formClasses: getCssClasses(target),
                elements: elementsData_1,
            }), resolveDynamicContext(context, target, elementsData_1));
            flushPendingCookies();
        }
    };
}

var _trackers = {};
/**
 * A plugin which enables automatic form focus, change, and submit tracking
 */
function FormTrackingPlugin() {
    return {
        activateBrowserPlugin: function (tracker) {
            _trackers[tracker.id] = tracker;
        },
    };
}
/**
 * Enables automatic form tracking.
 *
 * An event will be fired when a form field is changed or a form submitted.
 * This can be called multiple times: previous listeners will be removed and replaced with any updated configuration.
 *
 * @param configuration - The form tracking configuration
 * @param trackers - The tracker identifiers which the events will be sent to
 */
function enableFormTracking(configuration, trackers) {
    if (configuration === void 0) { configuration = {}; }
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackers.forEach(function (t) {
        if (_trackers[t]) {
            removeFormListeners(_trackers[t]);
            addFormListeners(_trackers[t], configuration);
        }
    });
}
/**
 * Disables automatic form tracking.
 *
 * All page-level listeners for the given trackers will be removed.
 *
 * @param trackers - The tracker identifiers which the events will be sent to
 */
function disableFormTracking(trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackers.forEach(function (t) {
        if (_trackers[t]) {
            removeFormListeners(_trackers[t]);
        }
    });
}

export { FormTrackingPlugin, disableFormTracking, enableFormTracking };
//# sourceMappingURL=index.module.js.map
